import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../firebase';

/**
 * Sends a login summary notification to the support team when a patient logs in.
 * Only sends for returning users and respects a cooldown period to prevent spam.
 * 
 * @param {Object} user - The Firebase Auth user object
 * @returns {Promise<boolean>} - Whether a notification was sent
 */
export const sendLoginSummaryToSupport = async (user) => {
  if (!user || !user.uid) {
    console.log('No valid user provided for login notification');
    return false;
  }

  try {
    console.log('Checking if login notification should be sent for user:', user.uid);
    
    // Get user data from Firestore
    const userDoc = await getDoc(doc(db, 'patients', user.uid));
    
    if (!userDoc.exists()) {
      console.log('User document not found, skipping login notification');
      return false;
    }
    
    const userData = userDoc.data();
    
    // Only send summary email if:
    // 1. User has set own password (not first login)
    // 2. First login was more than 1 hour ago
    // 3. We haven't sent a summary email recently (to avoid spamming)
    const hasSetPassword = userData.hasSetOwnPassword === true;
    const passwordSetupTime = userData.ownPasswordSetUpAt?.toDate();
    const isReturningUser = passwordSetupTime && 
                           (new Date() - passwordSetupTime) > (60 * 60 * 1000); // 1 hour in ms
    
    const lastEmailTime = userData.lastLoginSummaryEmailSent?.toDate();
    const emailCooldown = 12 * 60 * 60 * 1000; // 12 hours in ms
    // const emailCooldown = 10 * 1000; // 10 seconds in ms FOR TESTING
    const canSendEmail = !lastEmailTime || 
                        (new Date() - lastEmailTime) > emailCooldown;
    
    if (hasSetPassword && isReturningUser && canSendEmail) {
      console.log('Sending login summary to support...');
      // Call the Cloud Function to send the summary email
      const result = await httpsCallable(functions, 'sendLoginSummaryEmail')();
      console.log('Support notification sent:', result.data);
      return true;
    } else {
      console.log('Skipping login notification:', {
        hasSetPassword,
        isReturningUser: Boolean(isReturningUser),
        canSendEmail,
        timeSinceLastEmail: lastEmailTime ? (new Date() - lastEmailTime) / (60 * 60 * 1000) + ' hours' : 'never sent'
      });
      return false;
    }
  } catch (error) {
    console.error('Error sending login notification:', error);
    // Don't throw - this is a non-critical feature
    return false;
  }
};